<div class="p-6">
    <!-- Header -->
    <div class="flex items-start gap-3 mb-5">
        <div class="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <mat-icon class="!w-5 !h-5 !text-[20px] leading-5 text-blue-600">sync_alt</mat-icon>
        </div>
        <div>
            <h2 class="text-base font-semibold text-slate-900">{{ 'siteMapping.title' | translate }}</h2>
            <p class="text-sm text-slate-500 mt-0.5">{{ data.parkingSpace.name }}</p>
        </div>
    </div>

    <p class="text-sm text-slate-500 mb-5">{{ 'siteMapping.intro' | translate }}</p>

    <!-- Editor -->
    <div class="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-6">
        <div class="flex items-center gap-2 mb-4">
            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-slate-400">
                {{ isEditing() ? 'edit' : 'add_circle_outline' }}
            </mat-icon>
            <h3 class="text-sm font-semibold text-slate-900">
                {{ (isEditing() ? 'siteMapping.formTitleEdit' : 'siteMapping.formTitleCreate') | translate }}
            </h3>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Old site id -->
            <div>
                <label class="text-sm font-medium text-slate-900" for="sm-old-site">
                    {{ 'siteMapping.oldSiteId' | translate }}
                </label>
                <div class="text-xs text-slate-500 mt-0.5 mb-2">{{ 'siteMapping.oldSiteIdHint' | translate }}</div>
                <input id="sm-old-site" type="number" min="0" step="1"
                    class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                    [ngModel]="oldParkingSiteId()"
                    (ngModelChange)="oldParkingSiteId.set($event === '' || $event === null ? null : $event)" />
            </div>

            <!-- Parking type definition -->
            <div>
                <label class="text-sm font-medium text-slate-900" for="sm-type">
                    {{ 'siteMapping.parkingTypeDefinition' | translate }}
                </label>
                <div class="text-xs text-slate-500 mt-0.5 mb-2">{{ 'siteMapping.parkingTypeDefinitionHint' | translate }}</div>
                <div class="relative">
                    <select id="sm-type"
                        class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600 disabled:bg-slate-100 disabled:text-slate-400"
                        [disabled]="isLoadingTypes()" [ngModel]="newParkingTypeDefinitionId()"
                        (ngModelChange)="newParkingTypeDefinitionId.set($event)">
                        <option value="">{{ 'siteMapping.parkingTypeDefinitionPlaceholder' | translate }}</option>
                        @for (type of parkingTypes(); track type.id) {
                        <option [value]="type.id">{{ type.name }}@if (type.type) { · {{ type.type }} }</option>
                        }
                    </select>
                    @if (isLoadingTypes()) {
                    <mat-spinner class="!absolute right-2 top-2.5" diameter="16"></mat-spinner>
                    }
                </div>
            </div>

            <!-- Zone -->
            <div>
                <label class="text-sm font-medium text-slate-900" for="sm-zone">
                    {{ 'siteMapping.zone' | translate }}
                </label>
                <div class="text-xs text-slate-500 mt-0.5 mb-2">{{ 'siteMapping.zoneHint' | translate }}</div>
                <select id="sm-zone"
                    class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                    [ngModel]="newParkingZoneId()" (ngModelChange)="newParkingZoneId.set($event)">
                    <option value="">{{ 'siteMapping.allZones' | translate }}</option>
                    @for (zone of zones; track zone.id) {
                    <option [value]="zone.id">{{ zone.name }}</option>
                    }
                </select>
            </div>

            <!-- Description -->
            <div>
                <label class="text-sm font-medium text-slate-900" for="sm-description">
                    {{ 'siteMapping.description' | translate }}
                </label>
                <div class="text-xs text-slate-500 mt-0.5 mb-2">{{ 'siteMapping.descriptionHint' | translate }}</div>
                <input id="sm-description" type="text" maxlength="200"
                    class="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-600"
                    [placeholder]="'siteMapping.descriptionPlaceholder' | translate" [ngModel]="description()"
                    (ngModelChange)="description.set($event)" />
            </div>
        </div>

        <!-- Enabled -->
        <div class="flex items-center justify-between py-3 mt-2 border-t border-slate-200">
            <div class="pr-4">
                <div class="text-sm font-medium text-slate-900">{{ 'siteMapping.enabled' | translate }}</div>
                <div class="text-xs text-slate-500 mt-0.5">{{ 'siteMapping.enabledHint' | translate }}</div>
            </div>
            <label class="relative inline-flex items-center cursor-pointer shrink-0">
                <input type="checkbox" class="sr-only peer" [checked]="enabled()"
                    (change)="enabled.set($any($event.target).checked)" />
                <div
                    class="w-11 h-6 bg-slate-200 rounded-full transition-colors peer-checked:bg-blue-600 peer-focus:ring-2 peer-focus:ring-blue-100 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:border after:border-slate-300 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white">
                </div>
            </label>
        </div>

        @if (isDuplicate()) {
        <div class="flex gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs mb-3">
            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 shrink-0 mt-0.5">warning</mat-icon>
            <span>{{ 'siteMapping.duplicateWarning' | translate }}</span>
        </div>
        }

        @if (!isEditing()) {
        <div class="flex gap-2 p-3 rounded-lg bg-slate-100 border border-slate-200 text-slate-600 text-xs mb-3">
            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 shrink-0 mt-0.5">info</mat-icon>
            <span>{{ 'siteMapping.siteLevelNote' | translate }}</span>
        </div>
        }

        <div class="flex justify-end gap-2">
            @if (isEditing()) {
            <jjames-button variant="ghost" (clicked)="cancelEdit()">
                {{ 'siteMapping.cancelEdit' | translate }}
            </jjames-button>
            }
            <jjames-button variant="primary" [disabled]="!canSave()" [loading]="isSaving()" (clicked)="save()">
                {{ (isEditing() ? 'siteMapping.saveChanges' : 'siteMapping.create') | translate }}
            </jjames-button>
        </div>
    </div>

    <!-- List -->
    <div class="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div class="flex items-center gap-2">
            <h3 class="text-sm font-semibold text-slate-900">{{ 'siteMapping.listTitle' | translate }}</h3>
            <span class="text-xs font-medium text-slate-400">({{ visibleMappings().length }})</span>
        </div>
        <div class="flex items-center gap-2">
            <div class="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
                <button type="button" (click)="scope.set('space')"
                    class="text-xs font-medium px-2.5 py-1 rounded-md transition-colors"
                    [ngClass]="scope() === 'space' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'">
                    {{ 'siteMapping.scopeThisSpace' | translate }}
                </button>
                <button type="button" (click)="scope.set('all')"
                    class="text-xs font-medium px-2.5 py-1 rounded-md transition-colors"
                    [ngClass]="scope() === 'all' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'">
                    {{ 'siteMapping.scopeAllSpaces' | translate }}
                </button>
            </div>
            <button type="button" (click)="loadMappings()" [disabled]="isLoadingMappings()"
                [matTooltip]="'common.refresh' | translate"
                class="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors px-3 py-1.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">refresh</mat-icon>
            </button>
        </div>
    </div>

    @if (isLoadingMappings()) {
    <div class="flex justify-center py-10">
        <mat-spinner diameter="32"></mat-spinner>
    </div>
    } @else if (visibleMappings().length === 0) {
    <div class="text-center py-10 rounded-xl border border-dashed border-slate-200 bg-slate-50">
        <mat-icon class="!w-8 !h-8 !text-[32px] leading-8 text-slate-300">sync_disabled</mat-icon>
        <p class="text-sm font-medium text-slate-700 mt-2">{{ 'siteMapping.empty' | translate }}</p>
        <p class="text-xs text-slate-500 mt-1">{{ 'siteMapping.emptyHint' | translate }}</p>
    </div>
    } @else {
    <div class="overflow-x-auto rounded-xl border border-slate-200">
        <table class="w-full text-sm">
            <thead class="bg-slate-50 border-b border-slate-200">
                <tr class="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    <th class="px-3 py-2.5 whitespace-nowrap">{{ 'siteMapping.colOldSite' | translate }}</th>
                    <th class="px-3 py-2.5 whitespace-nowrap">{{ 'siteMapping.colLevel' | translate }}</th>
                    <th class="px-3 py-2.5 whitespace-nowrap">{{ 'siteMapping.colType' | translate }}</th>
                    <th class="px-3 py-2.5 whitespace-nowrap">{{ 'siteMapping.colZone' | translate }}</th>
                    @if (scope() === 'all') {
                    <th class="px-3 py-2.5 whitespace-nowrap">{{ 'siteMapping.colSpace' | translate }}</th>
                    }
                    <th class="px-3 py-2.5 whitespace-nowrap">{{ 'siteMapping.colStatus' | translate }}</th>
                    <th class="px-3 py-2.5">{{ 'siteMapping.colDescription' | translate }}</th>
                    <th class="px-3 py-2.5 text-right whitespace-nowrap">{{ 'siteMapping.colActions' | translate }}</th>
                </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
                @for (mapping of visibleMappings(); track mapping.id ?? $index) {
                <tr class="hover:bg-slate-50 transition-colors"
                    [ngClass]="editingId() === mapping.id ? 'bg-blue-50' : 'bg-white'">
                    <td class="px-3 py-2.5 font-medium text-slate-900 whitespace-nowrap">
                        {{ mapping.oldParkingSiteId }}
                        @if (mapping.oldParkingRightId !== null && mapping.oldParkingRightId !== undefined) {
                        <span class="text-slate-400"> / {{ mapping.oldParkingRightId }}</span>
                        }
                    </td>
                    <td class="px-3 py-2.5 whitespace-nowrap">
                        @if (mapping.oldParkingRightId !== null && mapping.oldParkingRightId !== undefined) {
                        <span class="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700">
                            {{ 'siteMapping.levelRight' | translate }}
                        </span>
                        } @else {
                        <span class="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                            {{ 'siteMapping.levelSite' | translate }}
                        </span>
                        }
                    </td>
                    <td class="px-3 py-2.5 text-slate-700">{{ typeName(mapping.newParkingTypeDefinitionId) }}</td>
                    <td class="px-3 py-2.5 text-slate-700">{{ zoneName(mapping) }}</td>
                    @if (scope() === 'all') {
                    <td class="px-3 py-2.5 text-slate-700">{{ spaceName(mapping) }}</td>
                    }
                    <td class="px-3 py-2.5 whitespace-nowrap">
                        @if (mapping.enabled) {
                        <span class="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
                            <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                            {{ 'siteMapping.statusEnabled' | translate }}
                        </span>
                        } @else {
                        <span class="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                            <span class="w-1.5 h-1.5 rounded-full bg-slate-300"></span>
                            {{ 'siteMapping.statusDisabled' | translate }}
                        </span>
                        }
                    </td>
                    <td class="px-3 py-2.5 text-slate-500">
                        {{ mapping.description || '—' }}
                    </td>
                    <td class="px-3 py-2.5 text-right whitespace-nowrap">
                        @if (!isEditable(mapping)) {
                        <span class="text-xs text-slate-400" [matTooltip]="'siteMapping.otherSpaceHint' | translate">
                            {{ 'siteMapping.readOnly' | translate }}
                        </span>
                        } @else if (pendingDeleteId() === mapping.id) {
                        <div class="inline-flex items-center gap-2">
                            <span class="text-xs text-slate-600">{{ 'siteMapping.deleteConfirm' | translate }}</span>
                            <button type="button" (click)="confirmDelete(mapping)" [disabled]="deletingId() === mapping.id"
                                class="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors px-2.5 py-1 rounded-md disabled:opacity-50">
                                {{ 'siteMapping.deleteYes' | translate }}
                            </button>
                            <button type="button" (click)="cancelDelete()"
                                class="text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors px-2.5 py-1 rounded-md">
                                {{ 'siteMapping.deleteNo' | translate }}
                            </button>
                        </div>
                        } @else {
                        <div class="inline-flex items-center gap-1">
                            <button type="button" (click)="startEdit(mapping)"
                                [matTooltip]="'siteMapping.editAria' | translate"
                                class="w-7 h-7 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                                <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">edit</mat-icon>
                            </button>
                            <button type="button" (click)="askDelete(mapping)"
                                [matTooltip]="'siteMapping.deleteAria' | translate"
                                class="w-7 h-7 inline-flex items-center justify-center rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors">
                                <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">delete</mat-icon>
                            </button>
                        </div>
                        }
                    </td>
                </tr>
                }
            </tbody>
        </table>
    </div>
    }

    <div class="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
        <jjames-button variant="ghost" (clicked)="close()">{{ 'siteMapping.close' | translate }}</jjames-button>
    </div>
</div>
